/**
 * Ocean Booking Header Fields Component
 *
 * Renders the header section of the Ocean Booking form.
 * Includes booking request fields and conditionally shows
 * carrier response fields when present.
 *
 * Fields:
 * - Request: bookingRequestNumber, expectedDepartureDate, expectedArrivalDate,
 *   freightPaymentTermCode, transportDocumentTypeCode, incoTerms,
 *   receiptTypeAtOrigin, deliveryTypeAtDestination, serviceContractReference,
 *   declaredValue, declaredValueCurrency
 * - Response (shown when present): carrierBookingReference, bookingStatus,
 *   carrierCode, carrierServiceCode, carrierServiceName, transportDocumentReference
 */

import Input from '../common/Input';
import { getFieldIssues } from '../../utils/confidenceUtils';

/**
 * Freight payment term options (DCSA standard codes)
 */
const FREIGHT_PAYMENT_TERMS = [
  { value: '', label: 'Select...' },
  { value: 'PRE', label: 'PRE - Prepaid' },
  { value: 'COL', label: 'COL - Collect' }
];

/**
 * Transport document type options
 */
const TRANSPORT_DOC_TYPES = [
  { value: '', label: 'Select...' },
  { value: 'BOL', label: 'BOL - Bill of Lading' },
  { value: 'SWB', label: 'SWB - Sea Waybill' }
];

/**
 * Incoterms 2020 options
 */
const INCOTERMS_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'EXW', label: 'EXW - Ex Works' },
  { value: 'FCA', label: 'FCA - Free Carrier' },
  { value: 'CPT', label: 'CPT - Carriage Paid To' },
  { value: 'CIP', label: 'CIP - Carriage and Insurance Paid To' },
  { value: 'DAP', label: 'DAP - Delivered at Place' },
  { value: 'DPU', label: 'DPU - Delivered at Place Unloaded' },
  { value: 'DDP', label: 'DDP - Delivered Duty Paid' },
  { value: 'FAS', label: 'FAS - Free Alongside Ship' },
  { value: 'FOB', label: 'FOB - Free on Board' },
  { value: 'CFR', label: 'CFR - Cost and Freight' },
  { value: 'CIF', label: 'CIF - Cost, Insurance and Freight' }
];

/**
 * Receipt/Delivery type options (DCSA standard)
 */
const RECEIPT_DELIVERY_TYPES = [
  { value: '', label: 'Select...' },
  { value: 'CY', label: 'CY - Container Yard' },
  { value: 'SD', label: 'SD - Store Door' },
  { value: 'CFS', label: 'CFS - Container Freight Station' }
];

/**
 * Severity color map for inline issue indicators on select fields
 */
const SEVERITY_COLORS = {
  error:   { border: 'border-l-red-500',   text: 'text-red-600',   icon: 'text-red-500' },
  warning: { border: 'border-l-amber-500', text: 'text-amber-600', icon: 'text-amber-500' },
  info:    { border: 'border-l-blue-500',  text: 'text-blue-600',  icon: 'text-blue-500' },
};

function formatIssueType(type) {
  if (!type) return 'Issue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Inline issue indicator for select fields
 */
function IssueIndicator({ issues = [] }) {
  if (issues.length === 0) return null;
  const worst = issues.some(i => i.severity === 'error') ? 'error'
    : issues.some(i => i.severity === 'warning') ? 'warning' : 'info';
  return (
    <span className="relative group/issues inline-flex">
      <svg className={`w-3.5 h-3.5 ${SEVERITY_COLORS[worst]?.icon || 'text-amber-500'} cursor-help`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/issues:block w-64">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {issues.map((issue, idx) => (
            <div key={idx} className={`px-3 py-2 border-l-4 ${SEVERITY_COLORS[issue.severity]?.border || 'border-l-gray-300'} ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
              <p className={`text-xs font-semibold ${SEVERITY_COLORS[issue.severity]?.text || 'text-gray-600'}`}>{formatIssueType(issue.type)}</p>
              {issue.detail && <p className="text-xs text-gray-600 mt-0.5">{issue.detail}</p>}
            </div>
          ))}
        </div>
      </div>
    </span>
  );
}

/**
 * Helper to render a select field with issue indicator
 */
function SelectField({ label, fieldPath, register, registerPath, options, isEditMode, issues = [], errors }) {
  const fieldIssues = getFieldIssues(issues, fieldPath);
  const hasIssues = fieldIssues.length > 0;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        <span className="inline-flex items-center gap-1">
          {label}
          <IssueIndicator issues={fieldIssues} />
        </span>
      </label>
      <select
        {...register(registerPath)}
        disabled={!isEditMode}
        className={`
          w-full px-3 py-2 border rounded-md shadow-sm text-sm
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          ${hasIssues ? 'border-amber-400 bg-amber-50' : 'border-gray-300'}
          ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}
        `}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * OBHeaderFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 * @param {Object} [props.oceanBooking] - The booking data (for conditional rendering)
 */
function OBHeaderFields({ control, register, isEditMode, issues = [], errors = {}, oceanBooking = {} }) {
  // Determine if response fields should be shown (fields are nested under header)
  const header = oceanBooking.header || oceanBooking;
  const hasResponseFields = header.carrierBookingReference ||
    header.bookingStatus ||
    header.carrierCode ||
    header.carrierServiceCode ||
    header.carrierServiceName ||
    header.transportDocumentReference;

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
        Booking Request
      </h3>

      {/* Request fields grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Input
          label="Booking Request Number"
          {...register('header.bookingRequestNumber')}
          disabled={!isEditMode}
          issues={getFieldIssues(issues, 'header.bookingRequestNumber')}
        />

        <Input
          label="Expected Departure Date"
          type="date"
          {...register('header.expectedDepartureDate')}
          disabled={!isEditMode}
          issues={getFieldIssues(issues, 'header.expectedDepartureDate')}
        />

        <Input
          label="Expected Arrival Date"
          type="date"
          {...register('header.expectedArrivalDate')}
          disabled={!isEditMode}
          issues={getFieldIssues(issues, 'header.expectedArrivalDate')}
        />

        <SelectField
          label="Freight Payment Terms"
          fieldPath="header.freightPaymentTermCode"
          register={register}
          registerPath="header.freightPaymentTermCode"
          options={FREIGHT_PAYMENT_TERMS}
          isEditMode={isEditMode}
          issues={issues}
        />

        <SelectField
          label="Transport Document Type"
          fieldPath="header.transportDocumentTypeCode"
          register={register}
          registerPath="header.transportDocumentTypeCode"
          options={TRANSPORT_DOC_TYPES}
          isEditMode={isEditMode}
          issues={issues}
        />

        <SelectField
          label="Incoterms"
          fieldPath="header.incoTerms"
          register={register}
          registerPath="header.incoTerms"
          options={INCOTERMS_OPTIONS}
          isEditMode={isEditMode}
          issues={issues}
        />

        <SelectField
          label="Receipt Type at Origin"
          fieldPath="header.receiptTypeAtOrigin"
          register={register}
          registerPath="header.receiptTypeAtOrigin"
          options={RECEIPT_DELIVERY_TYPES}
          isEditMode={isEditMode}
          issues={issues}
        />

        <SelectField
          label="Delivery Type at Destination"
          fieldPath="header.deliveryTypeAtDestination"
          register={register}
          registerPath="header.deliveryTypeAtDestination"
          options={RECEIPT_DELIVERY_TYPES}
          isEditMode={isEditMode}
          issues={issues}
        />

        <Input
          label="Service Contract Reference"
          {...register('header.serviceContractReference')}
          disabled={!isEditMode}
          issues={getFieldIssues(issues, 'header.serviceContractReference')}
        />

        <Input
          label="Declared Value"
          type="number"
          {...register('header.declaredValue', { valueAsNumber: true })}
          disabled={!isEditMode}
          issues={getFieldIssues(issues, 'header.declaredValue')}
        />

        <Input
          label="Declared Value Currency"
          {...register('header.declaredValueCurrency')}
          disabled={!isEditMode}
          issues={getFieldIssues(issues, 'header.declaredValueCurrency')}
          placeholder="e.g., USD"
        />
      </div>

      {/* Response header fields -- only shown when carrier has responded */}
      {hasResponseFields && (
        <>
          <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mt-6">
            Carrier Response
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Input
              label="Carrier Booking Reference"
              {...register('header.carrierBookingReference')}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, 'header.carrierBookingReference')}
            />

            <Input
              label="Booking Status"
              {...register('header.bookingStatus')}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, 'header.bookingStatus')}
            />

            <Input
              label="Carrier Code"
              {...register('header.carrierCode')}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, 'header.carrierCode')}
            />

            <Input
              label="Carrier Service Code"
              {...register('header.carrierServiceCode')}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, 'header.carrierServiceCode')}
            />

            <Input
              label="Carrier Service Name"
              {...register('header.carrierServiceName')}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, 'header.carrierServiceName')}
            />

            <Input
              label="Transport Document Reference"
              {...register('header.transportDocumentReference')}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, 'header.transportDocumentReference')}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default OBHeaderFields;
